import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useLocalParticipant,
  useRemoteParticipants,
  useTracks,
  useIsSpeaking,
  useTrackMutedIndicator,
  useTrackToggle,
  useDisconnectButton,
  useMediaDeviceSelect,
  useChat,
  VideoTrack,
  isTrackReference,
  MicIcon,
  MicDisabledIcon,
  CameraIcon,
  CameraDisabledIcon,
  ScreenShareIcon,
  ScreenShareStopIcon
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track } from 'livekit-client';
import axios from 'axios';
import ClientRecorder from './ClientRecorder';
import MeetingLobby from './MeetingLobby';
import Avatar from './Avatar';
import Icon from './Icon';
import { useModal } from '../context/ModalContext';
import { getUserRole, USER_ROLES } from '../utils/rolePermissions';
import { applyFormat, renderFormattedText } from '../utils/simpleRichText';
import {
  NotesPanel,
  NotesIcon,
  ChevronDownIcon,
  BookmarkIcon,
  SendIcon,
  formatElapsed,
  formatWallClock
} from './MeetingNotesPanel';
import meetingLogo from '../icons/v-meeting-logo.png';
// import BulletproofClientRecorder from './BulletproofClientRecorder'; // REVERTED - using IndexedDB + Upload instead
import '../styles/LiveKitMeetingRoom.css';

// ------------------------------------------------------------------
// Small inline icons (not provided by @livekit/components-react)
// ------------------------------------------------------------------

const ChevronUpIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5 15L12 8L19 15"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Matches the lobby's People icon/label exactly (same Icon font glyph).
const ParticipantsIcon = () => <Icon name="user-add" size="18px" />;

// Matches the lobby's Chat icon exactly (same Icon font glyph).
const ChatBubbleIcon = () => <Icon name="comment-empty" size="18px" />;

const PersonSmallIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.75" />
    <path d="M4 20C4 16.134 7.58172 13 12 13C16.4183 13 20 16.134 20 20" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

// Recording-state icons for the pill's combined status/pause-toggle button
const RecordDotIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" fill="currentColor" />
  </svg>
);

const PauseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="4" width="5" height="16" rx="1.5" fill="currentColor" />
    <rect x="14" y="4" width="5" height="16" rx="1.5" fill="currentColor" />
  </svg>
);

const MoreDotsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="5" r="2" fill="currentColor" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
    <circle cx="12" cy="19" r="2" fill="currentColor" />
  </svg>
);

const getParticipantIsGuest = (participant) => {
  try {
    const meta = participant?.metadata ? JSON.parse(participant.metadata) : null;
    return !!meta?.is_guest;
  } catch (e) {
    return false;
  }
};

// Mirrors MeetingLobby's isAgencySideParticipant - a non-guest participant is
// either someone on the agency side (shows the agency's name) or a client
// (shows the meeting's brand/organization name). There's exactly one agency
// and, at most, one client org per meeting, so both names are uniform across
// every participant on their respective side.
const AGENCY_SIDE_ROLES = ['organizer', USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATOR];

const getParticipantOrgLabel = (participant, meetingParticipants, agencyName, brandName) => {
  const identity = participant.identity;
  const meta = (meetingParticipants || []).find(
    (p) => p.user_id === identity || p.participant_identity === identity
  );
  // No match (e.g. the meeting's creator, who isn't necessarily listed in
  // participants[]) defaults to the agency side, matching the old behavior
  // where an unmatched non-guest was assumed to be the host.
  const isAgencySide = meta ? !meta.is_external && AGENCY_SIDE_ROLES.includes(meta.role) : true;
  return isAgencySide ? agencyName : brandName || '';
};

// Single participant tile: video (or avatar placeholder), name/org label, mic status bar
const ParticipantTileCustom = ({ cameraTrackRef, micTrackRef, micGain, agencyId, agencyName, brandName, meetingParticipants }) => {
  const participant = cameraTrackRef.participant;
  const isSpeaking = useIsSpeaking(participant);
  const { isMuted } = useTrackMutedIndicator(micTrackRef);
  // Toggling the camera off mid-call mutes the published track rather than
  // unpublishing it, so isTrackReference alone stays true and <VideoTrack>
  // renders a frozen black frame instead of falling back to the avatar -
  // only true when joining with the camera already off (never published,
  // so useTracks returns a placeholder instead of a track reference).
  const { isMuted: isCameraMuted } = useTrackMutedIndicator(cameraTrackRef);
  const hasCamera = isTrackReference(cameraTrackRef) && !isCameraMuted;
  const [audioLevel, setAudioLevel] = useState(0);

  // Live mic level for every participant, rendered as the vertical meter bar
  // next to their name (LiveKit keeps participant.audioLevel updated for
  // remote participants too, via active-speaker updates).
  useEffect(() => {
    const updateAudioLevel = () => {
      let level = Math.pow(participant.audioLevel || 0, 0.5) * 100;
      if (participant.isLocal) level *= micGain;
      setAudioLevel(Math.min(level, 100));
    };

    const interval = setInterval(updateAudioLevel, 100);
    return () => clearInterval(interval);
  }, [participant, micGain]);

  const isGuest = getParticipantIsGuest(participant);

  const displayName = participant.name || participant.identity || 'Participant';
  const initials = displayName
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const orgLabel = isGuest ? 'Guest' : getParticipantOrgLabel(participant, meetingParticipants, agencyName, brandName);

  const meterState = isMuted ? 'muted' : audioLevel >= 85 ? 'hot' : audioLevel >= 60 ? 'warning' : 'good';

  return (
    <div className={`velvet-tile ${isSpeaking ? 'is-speaking' : ''}`}>
      {hasCamera ? (
        <VideoTrack trackRef={cameraTrackRef} className="velvet-tile-video" />
      ) : (
        <div className="velvet-tile-placeholder">
          {isGuest ? (
            <div className="velvet-tile-avatar">{initials}</div>
          ) : (
            // participant.identity is the user's _id for non-guest participants (see generate_token).
            // agency_id is required to look up anyone but a super admin (see get_user_by_id),
            // so it must be passed through even for remote participants.
            <Avatar user={{ _id: participant.identity, name: displayName, agency_id: agencyId }} size="xl" />
          )}
        </div>
      )}
      <div className="velvet-tile-label">
        <div className="velvet-tile-label-text">
          <span className="velvet-tile-name">{displayName}</span>
          <span className="velvet-tile-role">{orgLabel}</span>
        </div>
        <div
          className={`velvet-tile-status-bar velvet-tile-status-bar--${meterState}`}
          style={{ '--vm-level': `${Math.max(6, audioLevel)}%` }}
          title="Microphone level"
        />
      </div>
    </div>
  );
};

// Full-bleed tile for the active screen share track.
const ScreenShareTile = ({ trackRef }) => {
  const displayName = trackRef.participant.name || trackRef.participant.identity || 'Participant';
  return (
    <div className="velvet-screenshare-tile">
      <VideoTrack trackRef={trackRef} className="velvet-screenshare-video" />
      <div className="velvet-screenshare-label">{displayName} is presenting</div>
    </div>
  );
};

// Side rail only has room for a handful of thumbnails before they'd get too
// small to read - beyond this, the rest collapse into a single "+N" tile,
// matching Google Meet's behavior when a screen share is active.
const MAX_FILMSTRIP_TILES = 9;

// Per Figma: the rail is 1 column up to 2 tiles, 2 columns up to 6 (2 on top,
// 2 in the middle, 2 on the bottom at 4), and 3 columns beyond that (3/3/3 at
// 9) - tiles shrink to keep a fixed rail width as columns increase, and
// shrink vertically as rows increase, rather than growing the rail itself.
const getFilmstripCols = (count) => {
  if (count <= 2) return 1;
  if (count <= 6) return 2;
  return 3;
};

// Stand-in tile for the participants who don't get their own filmstrip slot.
const FilmstripOverflowTile = ({ count }) => (
  <div className="velvet-tile velvet-screenshare-overflow-tile">
    <span className="velvet-screenshare-overflow-count">+{count}</span>
  </div>
);

// Equal-width video grid: 2 participants = 50/50, 3 = 33/33/33, 4 = 25/25/25/25, wraps beyond that.
// Switches to a presentation layout (big screen-share tile + a camera filmstrip)
// whenever anyone is actively sharing their screen.
const VideoGrid = ({ micGain, agencyId, agencyName, brandName, meetingParticipants }) => {
  const cameraTracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }], {
    onlySubscribed: false
  });
  const micTracks = useTracks([{ source: Track.Source.Microphone, withPlaceholder: true }], {
    onlySubscribed: false
  });
  // withPlaceholder: false means this only contains entries for participants
  // with an actual published screen-share track (not a stand-in for everyone
  // else), so a non-empty list here means someone is currently sharing.
  const screenShareTracks = useTracks([{ source: Track.Source.ScreenShare, withPlaceholder: false }], {
    onlySubscribed: false
  });

  const micByIdentity = useMemo(() => {
    const map = new Map();
    micTracks.forEach((t) => map.set(t.participant.identity, t));
    return map;
  }, [micTracks]);

  const activeScreenShare = screenShareTracks[0];

  if (activeScreenShare) {
    const visibleCameraTracks = cameraTracks.slice(0, MAX_FILMSTRIP_TILES);
    const overflowCount = cameraTracks.length - visibleCameraTracks.length;
    const filmstripTileCount = visibleCameraTracks.length + (overflowCount > 0 ? 1 : 0);
    const filmstripCols = getFilmstripCols(filmstripTileCount);
    const filmstripRows = Math.max(1, Math.ceil(filmstripTileCount / filmstripCols));
    // Solo on the call (e.g. prepping a screen share before anyone joins) -
    // a single tile shouldn't stretch to fill the whole rail height the way
    // it would split evenly between 2+ tiles.
    const isSoloFilmstrip = filmstripTileCount <= 1;

    return (
      <div className="velvet-video-area velvet-video-area--screenshare">
        <ScreenShareTile trackRef={activeScreenShare} />
        <div
          className="velvet-screenshare-filmstrip"
          data-cols={filmstripCols}
          data-solo={isSoloFilmstrip || undefined}
          style={{ gridTemplateRows: `repeat(${filmstripRows}, 1fr)` }}
        >
          {visibleCameraTracks.map((trackRef) => (
            <ParticipantTileCustom
              key={trackRef.participant.identity}
              cameraTrackRef={trackRef}
              micTrackRef={micByIdentity.get(trackRef.participant.identity)}
              micGain={micGain}
              agencyId={agencyId}
              agencyName={agencyName}
              brandName={brandName}
              meetingParticipants={meetingParticipants}
            />
          ))}
          {overflowCount > 0 && <FilmstripOverflowTile count={overflowCount} />}
        </div>
      </div>
    );
  }

  const count = cameraTracks.length;
  // 1-2 participants get a centered "spotlight" layout with smaller,
  // portrait-cropped tiles instead of stretching edge-to-edge.
  const isSpotlight = count <= 2;
  const cols = count <= 4 ? Math.max(count, 1) : 4;
  const rows = Math.max(1, Math.ceil(count / cols));

  return (
    <div className="velvet-video-area">
      <div
        className={`velvet-video-grid ${isSpotlight ? 'velvet-video-grid--spotlight' : ''}`}
        style={
          isSpotlight
            ? undefined
            : {
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gridTemplateRows: `repeat(${rows}, 1fr)`
              }
        }
      >
        {cameraTracks.map((trackRef) => (
          <ParticipantTileCustom
            key={trackRef.participant.identity}
            cameraTrackRef={trackRef}
            micTrackRef={micByIdentity.get(trackRef.participant.identity)}
            micGain={micGain}
            agencyId={agencyId}
            agencyName={agencyName}
            brandName={brandName}
            meetingParticipants={meetingParticipants}
          />
        ))}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Header: logo, meeting title, recording indicator + timer
// ------------------------------------------------------------------

const MeetingHeader = ({ title, isRecording, isPaused, elapsedSeconds }) => (
  <div className="velvet-meeting-header">
    <div className="velvet-meeting-header-logo">
      <img src={meetingLogo} alt="Velvet Meetings" />
    </div>
    <div className="velvet-meeting-header-title">{title}</div>
    <div className="velvet-meeting-header-status">
      {isRecording && (
        <>
          <span className={`velvet-recording-dot ${isPaused ? 'paused' : ''}`} />
          <span className="velvet-recording-label">{isPaused ? 'Paused' : 'Recording'}</span>
          <span className="velvet-recording-timer">{formatElapsed(elapsedSeconds)}</span>
        </>
      )}
    </div>
  </div>
);


// ------------------------------------------------------------------
// Join-request card (agency only) - "X would like to join, let them in?"
// ------------------------------------------------------------------

const JoinRequestPrompt = ({ meetingId, onPendingChange }) => {
  const [pending, setPending] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await axios.get(`/api/livekit/meetings/${meetingId}/join-requests`, {
          params: { status: 'pending' },
          withCredentials: true
        });
        if (!cancelled && res.data.success) setPending(res.data.requests);
      } catch (err) {
        console.error('Failed to poll join requests:', err);
      }
    };

    poll();
    const id = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [meetingId]);

  useEffect(() => {
    onPendingChange?.(pending.length > 0);
  }, [pending, onPendingChange]);

  const respond = async (requestId, action) => {
    try {
      await axios.post(`/api/livekit/meetings/${meetingId}/join-requests/${requestId}/${action}`, {}, { withCredentials: true });
      setPending((prev) => prev.filter((r) => r.id !== requestId));
    } catch (err) {
      console.error(`Failed to ${action} join request:`, err);
    }
  };

  if (pending.length === 0) return null;

  return (
    <div className="velvet-join-request-stack">
      {pending.map((r) => (
        <div key={r.id} className="velvet-join-request-card">
          <div className="velvet-join-request-row">
            <div className="velvet-join-request-header">
              <span className="velvet-join-request-icon">
                <ParticipantsIcon />
              </span>
              <span>{r.name}</span>
            </div>
            <div className="velvet-join-request-text">Would like to join the meeting, let them in?</div>
            <div className="velvet-join-request-actions">
              <button type="button" className="velvet-jr-no" onClick={() => respond(r.id, 'deny')}>
                No
              </button>
              <button type="button" className="velvet-jr-yes" onClick={() => respond(r.id, 'approve')}>
                Yes
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ------------------------------------------------------------------
// Chat panel (agency + client) - real-time via LiveKit chat/data channel,
// persisted for history via REST.
// ------------------------------------------------------------------

const ChatPanel = ({ meetingId, agencyId, onToggle, onActivity }) => {
  const { localParticipant } = useLocalParticipant();
  const { send, chatMessages } = useChat();
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState('');
  const textareaRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    axios
      .get(`/api/livekit/meetings/${meetingId}/chat`, {
        params: agencyId ? { agency_id: agencyId } : {},
        withCredentials: true
      })
      .then((res) => {
        if (res.data.success) setHistory(res.data.messages);
      })
      .catch((err) => console.error('Failed to load chat history:', err));
  }, [meetingId, agencyId]);

  const combined = useMemo(() => {
    const fromHistory = history.map((m) => ({
      id: m.id,
      name: m.sender_name,
      text: m.text,
      ts: new Date(m.created_at).getTime()
    }));
    const fromLive = chatMessages.map((m) => ({
      id: m.id,
      name: m.from?.name || m.from?.identity || 'Participant',
      text: m.message,
      ts: m.timestamp
    }));

    const seen = new Set();
    const merged = [];
    [...fromHistory, ...fromLive].forEach((m) => {
      if (seen.has(m.id)) return;
      seen.add(m.id);
      merged.push(m);
    });
    merged.sort((a, b) => a.ts - b.ts);
    return merged;
  }, [history, chatMessages]);

  const seenCountRef = useRef(0);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [combined.length]);

  useEffect(() => {
    if (combined.length > seenCountRef.current) onActivity?.();
    seenCountRef.current = combined.length;
  }, [combined.length, onActivity]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');

    try {
      await send(text);
    } catch (err) {
      console.error('Failed to send chat message:', err);
      return;
    }

    axios
      .post(
        `/api/livekit/meetings/${meetingId}/chat`,
        {
          text,
          sender_name: localParticipant?.name || localParticipant?.identity || 'Participant',
          sender_identity: localParticipant?.identity || '',
          is_guest: getParticipantIsGuest(localParticipant),
          agency_id: agencyId
        },
        { withCredentials: true }
      )
      .catch((err) => console.error('Failed to persist chat message:', err));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const format = (marker) => {
    const result = applyFormat(textareaRef, marker);
    if (!result) return;
    setDraft(result.value);
    requestAnimationFrame(() => {
      textareaRef.current?.setSelectionRange(result.selectionStart, result.selectionEnd);
      textareaRef.current?.focus();
    });
  };

  return (
    <div className="velvet-side-panel velvet-chat-panel">
      <div className="velvet-panel-header">
        <span>
          <ChatBubbleIcon /> Chat
        </span>
        <button className="velvet-panel-collapse-btn" onClick={onToggle} title="Collapse">
          <ChevronDownIcon />
        </button>
      </div>

      <div className="velvet-panel-list" ref={listRef}>
        {combined.map((m) => (
          <div key={m.id} className="velvet-chat-entry">
            <div className="velvet-chat-entry-meta">
              <span className="velvet-chat-entry-name">{m.name}</span>
              <span className="velvet-chat-entry-time">{formatWallClock(m.ts)}</span>
            </div>
            <div className="velvet-chat-entry-text" dangerouslySetInnerHTML={{ __html: renderFormattedText(m.text) }} />
          </div>
        ))}
      </div>

      <div className="velvet-panel-compose">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message..."
        />
        <div className="velvet-panel-compose-toolbar">
          <button type="button" onClick={() => format('bold')}>
            <strong>B</strong>
          </button>
          <button type="button" onClick={() => format('italic')}>
            <em>I</em>
          </button>
          <button type="button" onClick={() => format('underline')}>
            <u>U</u>
          </button>
          <button type="button" className="velvet-panel-send" onClick={handleSend} title="Send">
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// People panel (agency only) - docked in the same column as Notes,
// matching the lobby's People panel chrome instead of a floating popover.
// ------------------------------------------------------------------

const MeetingPeoplePanel = ({ onToggle, agencyId, agencyName, brandName, meetingParticipants }) => {
  const cameraTracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }], {
    onlySubscribed: false
  });

  return (
    <div className="velvet-side-panel velvet-notes-panel">
      <div className="velvet-panel-header">
        <span>
          <ParticipantsIcon /> People
        </span>
        <button className="velvet-panel-collapse-btn" onClick={onToggle} title="Collapse">
          <ChevronDownIcon />
        </button>
      </div>
      <div className="velvet-panel-list">
        {cameraTracks.map((t) => {
          const p = t.participant;
          const isGuest = getParticipantIsGuest(p);
          const displayName = p.name || p.identity || 'Participant';
          const orgLabel = isGuest ? 'Guest' : getParticipantOrgLabel(p, meetingParticipants, agencyName, brandName);
          return (
            <div key={p.identity} className="velvet-people-row">
              {isGuest ? <PersonSmallIcon /> : <Avatar user={{ _id: p.identity, name: displayName, agency_id: agencyId }} size="sm" />}
              <span>{displayName}</span>
              <span className="velvet-people-role">{orgLabel}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Bottom bar: flush Notes/People (left) + Chat (right), matching the
// lobby's docked bottom bar, with the call controls floating as a pill
// that straddles the bar's top edge - half above it, half overlapping
// down into it (per the Figma spec: 606x76, 38px radius).
// ------------------------------------------------------------------

const MeetingBottomBar = ({
  isAgency,
  notesOpen,
  onToggleNotes,
  participantsOpen,
  onToggleParticipants,
  chatOpen,
  onToggleChat,
  hasPendingJoinRequests,
  hasChatActivity,
  micGain,
  setMicGain,
  isRecording,
  isPaused,
  onFlagMoment,
  onPauseToggle,
  onStopRecordingClick,
  onEndClick
}) => {
  const micToggle = useTrackToggle({ source: Track.Source.Microphone });
  const cameraToggle = useTrackToggle({ source: Track.Source.Camera });
  const screenShareToggle = useTrackToggle({ source: Track.Source.ScreenShare });
  const { buttonProps: disconnectProps } = useDisconnectButton({});
  const {
    devices: cameraDevices,
    activeDeviceId: activeCameraId,
    setActiveMediaDevice: setActiveCamera
  } = useMediaDeviceSelect({ kind: 'videoinput' });
  const [showVoiceMenu, setShowVoiceMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showCameraMenu, setShowCameraMenu] = useState(false);
  const voiceMenuRef = useRef(null);
  const moreMenuRef = useRef(null);
  const cameraMenuRef = useRef(null);

  const handleGainChange = (e) => {
    const newGain = parseFloat(e.target.value);
    setMicGain(newGain);
    localStorage.setItem('livekit_mic_gain', newGain.toString());
  };

  // Close the voice controls / more-options menus on outside click
  useEffect(() => {
    if (!showVoiceMenu) return;
    const handleClickOutside = (e) => {
      if (voiceMenuRef.current && !voiceMenuRef.current.contains(e.target)) setShowVoiceMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVoiceMenu]);

  useEffect(() => {
    if (!showMoreMenu) return;
    const handleClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setShowMoreMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu]);

  useEffect(() => {
    if (!showCameraMenu) return;
    const handleClickOutside = (e) => {
      if (cameraMenuRef.current && !cameraMenuRef.current.contains(e.target)) setShowCameraMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCameraMenu]);

  const handleSelectCamera = async (deviceId) => {
    setShowCameraMenu(false);
    try {
      await setActiveCamera(deviceId);
      if (!cameraToggle.enabled) await cameraToggle.toggle(true);
    } catch (err) {
      console.error('Failed to switch camera device:', err);
    }
  };

  const handleEnd = async () => {
    if (isAgency) {
      const confirmed = await onEndClick();
      if (!confirmed) return;
    }
    disconnectProps.onClick();
  };

  const handleStopRecording = async () => {
    setShowMoreMenu(false);
    await onStopRecordingClick();
  };

  return (
    <div className="velvet-bottom-bar">
      <div className="velvet-bottom-bar-left">
        {isAgency && (
          <>
            <button className={`velvet-tray-btn ${notesOpen ? 'active' : ''}`} onClick={onToggleNotes} title="Notes">
              <NotesIcon />
            </button>
            <button
              className={`velvet-tray-btn ${participantsOpen ? 'active' : ''}`}
              onClick={onToggleParticipants}
              title="People"
            >
              <ParticipantsIcon />
              {hasPendingJoinRequests && <span className="velvet-tray-badge" />}
            </button>
          </>
        )}
      </div>

      <div className="velvet-bottom-bar-pill">
        <div className="velvet-control-group" ref={voiceMenuRef}>
          <button
            {...micToggle.buttonProps}
            className={`velvet-control-btn ${micToggle.enabled ? '' : 'off'}`}
            title={micToggle.enabled ? 'Mute microphone' : 'Unmute microphone'}
          >
            {micToggle.enabled ? <MicIcon /> : <MicDisabledIcon />}
          </button>
          <button
            className={`velvet-control-caret ${showVoiceMenu ? 'active' : ''}`}
            onClick={() => setShowVoiceMenu((v) => !v)}
            title="Voice controls"
          >
            <ChevronUpIcon />
          </button>

          {showVoiceMenu && (
            <div className="voice-controls-menu">
              <div className="voice-controls-menu-header">Voice Controls</div>
              <div className="gain-control">
                <label className="gain-label">Mic Boost: {micGain.toFixed(1)}x</label>
                <input
                  type="range"
                  min="0.5"
                  max="3.0"
                  step="0.1"
                  value={micGain}
                  onChange={handleGainChange}
                  className="gain-slider"
                />
                <div className="gain-marks">
                  <span>0.5x</span>
                  <span>1.5x</span>
                  <span>3.0x</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="velvet-pill-divider" />

        <div className="velvet-control-group" ref={cameraMenuRef}>
          <button
            {...cameraToggle.buttonProps}
            className={`velvet-control-btn ${cameraToggle.enabled ? '' : 'off'}`}
            title={cameraToggle.enabled ? 'Turn off camera' : 'Turn on camera'}
          >
            {cameraToggle.enabled ? <CameraIcon /> : <CameraDisabledIcon />}
          </button>
          <button
            className={`velvet-control-caret ${showCameraMenu ? 'active' : ''}`}
            onClick={() => setShowCameraMenu((v) => !v)}
            title="Camera options"
          >
            <ChevronUpIcon />
          </button>

          {showCameraMenu && (
            <div className="voice-controls-menu velvet-more-menu">
              <div className="voice-controls-menu-header">Camera</div>
              {cameraDevices.length === 0 ? (
                <div className="velvet-more-menu-item velvet-more-menu-empty">No cameras found</div>
              ) : (
                cameraDevices.map((device, i) => (
                  <button
                    key={device.deviceId}
                    className={`velvet-more-menu-item ${device.deviceId === activeCameraId ? 'active' : ''}`}
                    onClick={() => handleSelectCamera(device.deviceId)}
                  >
                    {device.label || `Camera ${i + 1}`}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="velvet-pill-divider" />

        <button
          {...screenShareToggle.buttonProps}
          className={`velvet-control-btn ${screenShareToggle.enabled ? 'active' : ''}`}
          title={screenShareToggle.enabled ? 'Stop screen share' : 'Share screen'}
        >
          {screenShareToggle.enabled ? <ScreenShareStopIcon /> : <ScreenShareIcon />}
        </button>

        {isAgency && (
          <>
            <div className="velvet-pill-divider" />
            <button className="velvet-control-btn" onClick={onFlagMoment} title="Flag moment">
              <BookmarkIcon />
            </button>
          </>
        )}

        {isAgency && (
          <>
            <div className="velvet-pill-divider" />
            {/* Recording/paused status itself lives in the header, next to
                the chat toggle - this is just the pause/resume action.
                Stays put (greyed out, not removed) whenever there's no
                active recording to pause/resume - before auto-start has
                fired, and after the recording has been stopped - instead
                of popping in and out of the pill. */}
            <button
              className="velvet-record-btn"
              onClick={onPauseToggle}
              disabled={!isRecording}
              title={!isRecording ? 'No active recording' : isPaused ? 'Resume recording' : 'Pause recording'}
            >
              {!isRecording || isPaused ? <RecordDotIcon /> : <PauseIcon />}
              <span className="velvet-record-label">Record</span>
            </button>

            <div className="velvet-pill-divider" />
            <div className="velvet-control-group" ref={moreMenuRef}>
              <button
                className={`velvet-control-btn ${showMoreMenu ? 'active' : ''}`}
                onClick={() => setShowMoreMenu((v) => !v)}
                disabled={!isRecording}
                title={isRecording ? 'More options' : 'No active recording'}
              >
                <MoreDotsIcon />
              </button>

              {showMoreMenu && isRecording && (
                <div className="voice-controls-menu velvet-more-menu">
                  <button
                    className="velvet-more-menu-item"
                    onClick={handleStopRecording}
                    title="Stop recording, stay in meeting"
                  >
                    Stop Recording
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        <button onClick={handleEnd} className="velvet-end-btn">
          {isAgency ? 'End' : 'Leave'}
        </button>
      </div>

      <div className="velvet-bottom-bar-right">
        <button className={`velvet-tray-btn ${chatOpen ? 'active' : ''}`} onClick={onToggleChat} title="Chat">
          <ChatBubbleIcon />
          {!chatOpen && hasChatActivity && <span className="velvet-tray-badge" />}
        </button>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Root in-room UI: header + notes/video/chat body + tray + control bar.
// Rendered as the sole child of <LiveKitRoom> so it can use room hooks.
// ------------------------------------------------------------------

const MeetingRoomUI = ({
  isAgency,
  meetingId,
  agencyId,
  agencyName,
  brandName,
  meetingParticipants,
  meetingTitle,
  isRecording,
  isPaused,
  recordingElapsedSeconds,
  micGain,
  setMicGain,
  onStopRecording,
  onEndMeeting,
  onPauseToggle
}) => {
  // Agency hosts land with Notes already open so they can jot things down
  // from the first second of the meeting without an extra click.
  const [notesOpen, setNotesOpen] = useState(isAgency);
  const [chatOpen, setChatOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [flagSignal, setFlagSignal] = useState(0);
  const [flaggedAtSeconds, setFlaggedAtSeconds] = useState(0);
  const [hasPendingJoinRequests, setHasPendingJoinRequests] = useState(false);
  const [hasChatActivity, setHasChatActivity] = useState(false);

  const handleFlagMoment = () => {
    setFlaggedAtSeconds(recordingElapsedSeconds);
    setFlagSignal((s) => s + 1);
    setNotesOpen(true);
  };

  // "End" closes the meeting for everyone - stops any active recording
  // server-side and tears down the room. Only the person who clicks it sees
  // a confirmation; returns whether they went through with it so the caller
  // knows whether to actually disconnect.
  const handleEndClick = async () => {
    return await onEndMeeting();
  };

  // "Stop Recording" only stops capturing - the room stays open and
  // everyone (including the host) stays connected, matching Squadcast's
  // behavior. Confirms since it's easy to mis-click mid-meeting.
  const handleStopRecordingClick = async () => {
    await onStopRecording();
  };

  return (
    <div className="velvet-meeting-layout">
      <MeetingHeader title={meetingTitle} isRecording={isRecording} isPaused={isPaused} elapsedSeconds={recordingElapsedSeconds} />

      <div className="velvet-meeting-body">
        {isAgency && (
          <div className={`velvet-notes-column ${(notesOpen || participantsOpen) ? '' : 'collapsed'}`}>
            {participantsOpen ? (
              <MeetingPeoplePanel
                onToggle={() => setParticipantsOpen(false)}
                agencyId={agencyId}
                agencyName={agencyName}
                brandName={brandName}
                meetingParticipants={meetingParticipants}
              />
            ) : (
              <NotesPanel
                meetingId={meetingId}
                onToggle={() => setNotesOpen(false)}
                flagSignal={flagSignal}
                flaggedAtSeconds={flaggedAtSeconds}
              />
            )}
          </div>
        )}

        {/* Floats independently of the notes column above (not nested inside
            it) so the host still sees it even when that column is collapsed -
            this is a notification, not panel content. */}
        {isAgency && <JoinRequestPrompt meetingId={meetingId} onPendingChange={setHasPendingJoinRequests} />}

        <VideoGrid
          micGain={micGain}
          agencyId={agencyId}
          agencyName={agencyName}
          brandName={brandName}
          meetingParticipants={meetingParticipants}
        />

        <div className={`velvet-chat-column ${chatOpen ? '' : 'collapsed'}`}>
          <ChatPanel
            meetingId={meetingId}
            agencyId={agencyId}
            onToggle={() => setChatOpen(false)}
            onActivity={() => setHasChatActivity(true)}
          />
        </div>
      </div>

      <MeetingBottomBar
        isAgency={isAgency}
        notesOpen={notesOpen}
        onToggleNotes={() => {
          setNotesOpen((o) => !o);
          setParticipantsOpen(false);
        }}
        participantsOpen={participantsOpen}
        onToggleParticipants={() => {
          setParticipantsOpen((o) => !o);
          setNotesOpen(false);
        }}
        chatOpen={chatOpen}
        onToggleChat={() => {
          setChatOpen((o) => !o);
          setHasChatActivity(false);
        }}
        hasPendingJoinRequests={hasPendingJoinRequests}
        hasChatActivity={hasChatActivity}
        micGain={micGain}
        setMicGain={setMicGain}
        isRecording={isRecording}
        isPaused={isPaused}
        onFlagMoment={handleFlagMoment}
        onPauseToggle={onPauseToggle}
        onStopRecordingClick={handleStopRecordingClick}
        onEndClick={handleEndClick}
      />
    </div>
  );
};

// Auto-starts recording once all expected participants have joined, and
// applies the mic-boost gain node to the local mic track.
const RoomControls = ({ isAgency, isRecording, expectedParticipants, micGain, startRecording }) => {
  const { microphoneTrack } = useLocalParticipant();
  // <LiveKitRoom>'s onParticipantConnected/onParticipantDisconnected aren't
  // real props of that component (they're silently ignored - it only
  // supports onConnected/onDisconnected/onError/onMediaDeviceFailure/
  // onEncryptionError), so the counter this used to rely on never moved off
  // 0. This hook is the real, reactively-updating participant list.
  const remoteParticipants = useRemoteParticipants();
  const [hasAutoStarted, setHasAutoStarted] = useState(false);
  const audioContextRef = useRef(null);
  const gainNodeRef = useRef(null);

  // Builds the gain graph once per published mic track and publishes its
  // output as the actual transmitted audio (via replaceTrack), so "Mic
  // Boost" changes what other participants hear. Previously the gain node
  // was built but never connected to anything downstream of it, so the only
  // thing it affected was the local self-view meter (which multiplies its
  // level by micGain) - your own bar would go red while the raw, un-boosted
  // mic track kept going out over the wire.
  useEffect(() => {
    if (!microphoneTrack?.track?.mediaStreamTrack) return;

    let cancelled = false;
    const rawTrack = microphoneTrack.track.mediaStreamTrack;
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(new MediaStream([rawTrack]));
    const gainNode = audioContext.createGain();
    gainNode.gain.value = micGain;
    // A virtual sink, not audioContext.destination - that would route the
    // user's own mic back out through their speakers (feedback).
    const destination = audioContext.createMediaStreamDestination();
    source.connect(gainNode);
    gainNode.connect(destination);

    audioContextRef.current = audioContext;
    gainNodeRef.current = gainNode;

    microphoneTrack.track.replaceTrack(destination.stream.getAudioTracks()[0], true).catch((err) => {
      if (!cancelled) console.error('Error publishing boosted mic track:', err);
    });

    return () => {
      cancelled = true;
      audioContext.close();
      audioContextRef.current = null;
      gainNodeRef.current = null;
    };
    // Rebuilt only when the published track itself changes - micGain updates
    // the existing node's value live, below, instead of rebuilding the graph.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [microphoneTrack]);

  // Live-adjust the existing gain node as the slider moves, without tearing
  // down and republishing the track on every tick.
  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = micGain;
    }
  }, [micGain]);

  useEffect(() => {
    // remoteParticipants only counts OTHER participants, while
    // expectedParticipants comes from the invite list, which includes the
    // organizer - add 1 for yourself. Restricted to the agency/host side so
    // guests (who can't call start-recording anyway - it requires a Velvet
    // session) don't also race to trigger it.
    if (
      isAgency &&
      !hasAutoStarted &&
      !isRecording &&
      expectedParticipants > 0 &&
      remoteParticipants.length + 1 >= expectedParticipants
    ) {
      setHasAutoStarted(true);
      startRecording();
    }
  }, [isAgency, remoteParticipants.length, expectedParticipants, hasAutoStarted, isRecording, startRecording]);

  return null;
};

// Component to track and save user's camera/mic preferences
const PreferenceTracker = () => {
  const videoTracks = useTracks([Track.Source.Camera], { onlySubscribed: false });
  const audioTracks = useTracks([Track.Source.Microphone], { onlySubscribed: false });

  // Watch for camera changes
  useEffect(() => {
    if (videoTracks.length > 0) {
      const videoTrack = videoTracks[0];
      const isEnabled = !videoTrack.publication.isMuted;
      localStorage.setItem('velvet_camera_enabled', isEnabled.toString());
    }
  }, [videoTracks]);

  // Watch for microphone changes
  useEffect(() => {
    if (audioTracks.length > 0) {
      const audioTrack = audioTracks[0];
      const isEnabled = !audioTrack.publication.isMuted;
      localStorage.setItem('velvet_mic_enabled', isEnabled.toString());
    }
  }, [audioTracks]);

  return null;
};

const LiveKitMeetingRoom = ({ meetingId, user, agencyId, onLeave }) => {
  const [token, setToken] = useState(null);
  const [wsUrl, setWsUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meetingInfo, setMeetingInfo] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // Set right before any app-driven room exit (this user ending/leaving, or
  // the room disconnecting because someone else ended it) so the unload
  // guard below only ever fires for a genuinely accidental tab close.
  const suppressUnloadWarningRef = useRef(false);
  const [expectedParticipants, setExpectedParticipants] = useState(0);
  const [displaySeconds, setDisplaySeconds] = useState(0);
  // Identity assigned by the join endpoint - local recording chunks are keyed
  // by it, so it has to be the same value stored in the meeting's participants
  const [participantIdentity, setParticipantIdentity] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [hostJoinError, setHostJoinError] = useState(null);

  const modal = useModal();

  const isAgency = !!user && getUserRole(user) !== USER_ROLES.CLIENT;
  const isClient = !!user && getUserRole(user) === USER_ROLES.CLIENT;

  // agencyId is the URL's agency_id param, which only exists so an
  // unauthenticated guest (no session) has a way to tell the backend which
  // agency owns this meeting. For anyone logged in - including a client -
  // the backend resolves the real agency_id from their session instead and
  // returns it on meetingInfo, which is the value avatar lookups need:
  // get_user_by_id requires the agency a non-super-admin user actually
  // belongs to, so falling back to the (often absent) URL param here was
  // silently breaking every participant's avatar except a super admin's.
  const resolvedAgencyId = meetingInfo?.agency_id || agencyId;

  // Same two names MeetingLobby already shows on the pre-join identity box -
  // there's one agency and, at most, one client org per meeting, so these
  // are uniform across every participant on their respective side (see
  // getParticipantOrgLabel).
  const agencyName = user?.agency_name || 'Velvet';
  const brandName = meetingInfo?.brand?.name || meetingInfo?.organization?.name || '';

  // Mic gain setting
  const [micGain, setMicGain] = useState(() => {
    const saved = localStorage.getItem('livekit_mic_gain');
    return saved !== null ? parseFloat(saved) : 1.0;
  });

  // Load camera/mic preferences from our own localStorage keys
  const getInitialVideoState = () => {
    const saved = localStorage.getItem('velvet_camera_enabled');
    if (saved !== null) {
      return saved === 'true';
    }
    return true; // Default for first-time users
  };

  const getInitialAudioState = () => {
    const saved = localStorage.getItem('velvet_mic_enabled');
    if (saved !== null) {
      return saved === 'true' ? {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      } : false;
    }
    // Default for first-time users
    return {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true
    };
  };

  useEffect(() => {
    if (isAgency || isClient) {
      fetchMeetingInfo();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId, isAgency, isClient]);

  // Recording start/pause/stop only ever updates isRecording/isPaused
  // locally for whoever clicked the button (the host) - poll the meeting's
  // server-side status so every participant (including clients and
  // unauthenticated guests) sees the live recording indicator in the
  // header. This is a legal-notice requirement, not just a nice-to-have,
  // so it has to reach everyone in the room, not only logged-in users.
  // agency_id is passed explicitly since guests have no session to
  // resolve it from - the endpoint falls back to it for exactly that case.
  useEffect(() => {
    if (!meetingId) return;

    const pollRecordingStatus = async () => {
      try {
        const res = await axios.get(`/api/livekit/meetings/${meetingId}`, {
          params: { agency_id: agencyId },
          withCredentials: true
        });
        if (res.data.success) {
          const meeting = res.data.meeting;
          setIsRecording(meeting.recording_status === 'recording');
          setIsPaused(!!meeting.is_paused);
        }
      } catch (err) {
        console.error('Error polling recording status:', err);
      }
    };

    pollRecordingStatus();
    const intervalId = setInterval(pollRecordingStatus, 5000);
    return () => clearInterval(intervalId);
  }, [meetingId, agencyId]);

  // Warn user before closing tab if recording is active
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRecording && !suppressUnloadWarningRef.current) {
        e.preventDefault();
        e.returnValue = 'Recording is in progress. Your local backup is still uploading. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRecording]);

  // Recording elapsed-time ticker for the header timer / flag-moment tagging
  useEffect(() => {
    if (!isRecording || isPaused) return;
    const id = setInterval(() => setDisplaySeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [isRecording, isPaused]);

  useEffect(() => {
    if (!isRecording) setDisplaySeconds(0);
  }, [isRecording]);

  // Loads meeting title/time/agenda for the host pre-meeting screen, without
  // connecting to the room yet.
  const fetchMeetingInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      const meetingResponse = await axios.get(`/api/livekit/meetings/${meetingId}`, {
        withCredentials: true
      });

      if (meetingResponse.data.success) {
        const meeting = meetingResponse.data.meeting;
        setMeetingInfo(meeting);

        // Set expected participant count
        const participantCount = meeting.participants?.length || 0;
        setExpectedParticipants(participantCount);

        // Check if recording is already active
        if (meeting.recording_status === 'recording') {
          setIsRecording(true);
        }
      } else {
        setError(meetingResponse.data.error || 'Failed to load meeting');
      }
    } catch (err) {
      console.error('Error loading meeting details:', err);
      setError(err.response?.data?.error || 'Failed to load meeting');
    } finally {
      setLoading(false);
    }
  };

  // Connects the agency/host or logged-in client to the room once they click
  // "Join Meeting" on the pre-meeting screen. Errors surface inline on that
  // screen so they can retry without losing their camera preview. Returns a
  // result object (rather than throwing) so MeetingLobby can tell a flat
  // failure apart from "not invited, but now waiting on the host" and drive
  // its own waiting/denied phases.
  const connectToRoom = async (recordingConsent = true) => {
    try {
      setConnecting(true);
      setHostJoinError(null);

      const response = await axios.post(
        `/api/livekit/meetings/${meetingId}/join`,
        { is_guest: false, recording_consent: recordingConsent },
        { withCredentials: true }
      );

      if (response.data.success) {
        setToken(response.data.token);
        setWsUrl(response.data.ws_url);
        setParticipantIdentity(response.data.participant_identity);
        return { success: true };
      } else {
        setHostJoinError(response.data.error || 'Failed to join meeting');
        return { success: false };
      }
    } catch (err) {
      console.error('Error joining meeting:', err);

      // Handle time-based access control errors
      if (err.response?.status === 403) {
        const errorData = err.response.data;
        const status = errorData.status;

        if (status === 'pending_approval') {
          // Not on the invite list - a join request was created (or already
          // existed) for this account. MeetingLobby takes it from here: show
          // the waiting screen and poll this request until the host responds.
          return { success: false, pendingApproval: true, requestId: errorData.request_id };
        }

        if (status === 'denied') {
          setHostJoinError('The host declined your request to join.');
        } else if (status === 'ended') {
          const endedAt = errorData.scheduled_end ? new Date(errorData.scheduled_end) : null;
          if (endedAt) {
            const timeStr = endedAt.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
            setHostJoinError(`This meeting ended at ${timeStr} and can no longer be joined.`);
          } else {
            setHostJoinError('This meeting has ended and can no longer be joined.');
          }
        } else {
          setHostJoinError(errorData.error || 'Access denied');
        }
      } else {
        setHostJoinError(err.response?.data?.error || 'Failed to join meeting');
      }
      return { success: false };
    } finally {
      setConnecting(false);
    }
  };

  const startRecording = async () => {
    try {
      const response = await axios.post(`/api/livekit/meetings/${meetingId}/start-recording`, {}, { withCredentials: true });
      if (response.data.success) setIsRecording(true);
    } catch (err) {
      console.error('Error starting recording:', err);
    }
  };

  const stopRecording = async ({ skipConfirm } = {}) => {
    if (!skipConfirm) {
      const confirmed = await modal.confirm(
        'Are you sure you want to stop recording? The recording will be saved and processed, and everyone will stay in the meeting.',
        'Stop Recording'
      );
      if (!confirmed) return false;
    }

    try {
      const response = await axios.post(`/api/livekit/meetings/${meetingId}/stop-recording`, {}, { withCredentials: true });
      if (response.data.success) {
        setIsRecording(false);
        setIsPaused(false);
        return true;
      }
    } catch (err) {
      console.error('Error stopping recording:', err);
    }
    return false;
  };

  const endMeeting = async ({ skipConfirm } = {}) => {
    if (!skipConfirm) {
      const confirmed = await modal.confirm(
        'Are you sure you want to end the meeting? This will stop any active recording and disconnect everyone.',
        'End Meeting'
      );
      if (!confirmed) return false;
    }

    try {
      const response = await axios.post(`/api/livekit/meetings/${meetingId}/end`, {}, { withCredentials: true });
      if (response.data.success) {
        setIsRecording(false);
        setIsPaused(false);
        return true;
      }
    } catch (err) {
      console.error('Error ending meeting:', err);
    }
    return false;
  };

  const handlePauseToggle = async () => {
    try {
      const endpoint = isPaused ? 'resume-recording' : 'pause-recording';
      const response = await axios.post(`/api/livekit/meetings/${meetingId}/${endpoint}`, {}, { withCredentials: true });
      if (response.data.success) setIsPaused(!isPaused);
    } catch (err) {
      console.error('Error toggling pause:', err);
    }
  };

  const handleGuestJoined = ({ token: guestToken, wsUrl: guestWsUrl, participantIdentity: guestParticipantIdentity, meeting }) => {
    setToken(guestToken);
    setWsUrl(guestWsUrl);
    setParticipantIdentity(guestParticipantIdentity);
    if (meeting) setMeetingInfo(meeting);
  };

  const handleDisconnect = () => {
    // The room is already gone by the time this fires, whether because this
    // user left/ended it or someone else did - never worth a "leave site?"
    // prompt on top of that.
    suppressUnloadWarningRef.current = true;
    if (onLeave) {
      onLeave();
    }
  };

  if (loading) {
    return (
      <div className="livekit-meeting-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Joining meeting...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="livekit-meeting-container">
        <div className="error-state">
          <h3>Unable to Join Meeting</h3>
          <p>{error}</p>
          <button onClick={onLeave} className="btn-primary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (isClient && !token) {
    return (
      <MeetingLobby
        meetingId={meetingId}
        agencyId={agencyId}
        variant="client"
        user={user}
        onJoin={connectToRoom}
        joining={connecting}
        joinError={hostJoinError}
      />
    );
  }

  if (!isAgency && !isClient && !token) {
    return <MeetingLobby meetingId={meetingId} agencyId={agencyId} onJoined={handleGuestJoined} />;
  }

  if (isAgency && !token) {
    return (
      <MeetingLobby
        meetingId={meetingId}
        agencyId={agencyId}
        variant="host"
        user={user}
        onJoin={connectToRoom}
        joining={connecting}
        joinError={hostJoinError}
      />
    );
  }

  if (!token || !wsUrl) {
    return (
      <div className="livekit-meeting-container">
        <div className="error-state">
          <h3>Meeting Access Error</h3>
          <p>Unable to get meeting credentials</p>
          <button onClick={onLeave} className="btn-primary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="livekit-meeting-container">
      <LiveKitRoom
        token={token}
        serverUrl={wsUrl}
        connect={true}
        onDisconnected={handleDisconnect}
        audio={getInitialAudioState()}
        video={getInitialVideoState()}
        onMediaDeviceFailure={(error) => {
          console.error('Media device error:', error);
        }}
        className="livekit-room"
      >
        <MeetingRoomUI
          isAgency={isAgency}
          meetingId={meetingId}
          agencyId={resolvedAgencyId}
          agencyName={agencyName}
          brandName={brandName}
          meetingParticipants={meetingInfo?.participants}
          meetingTitle={meetingInfo?.title}
          isRecording={isRecording}
          isPaused={isPaused}
          recordingElapsedSeconds={displaySeconds}
          micGain={micGain}
          setMicGain={setMicGain}
          onStopRecording={stopRecording}
          onEndMeeting={endMeeting}
          onPauseToggle={handlePauseToggle}
        />
        <RoomAudioRenderer />
        <PreferenceTracker />
        <RoomControls
          isAgency={isAgency}
          isRecording={isRecording}
          expectedParticipants={expectedParticipants}
          micGain={micGain}
          startRecording={startRecording}
        />
        <ClientRecorder
          meetingId={meetingId}
          agencyId={agencyId}
          participantIdentity={participantIdentity}
          isRecording={isRecording}
          isPaused={isPaused}
        />
        {/* BULLETPROOF LOCAL RECORDER - DISABLED (reverted to IndexedDB + Upload)
        <BulletproofClientRecorder
          meetingId={meetingId}
          participantIdentity={user?.id || user?.email || 'unknown'}
          participantName={user?.name || user?.email || 'Participant'}
          isRecording={isRecording}
          isPaused={isPaused}
          onSegmentSaved={(segmentNumber) => {
            setLocalRecordingSegments(segmentNumber);
          }}
          onMergeComplete={() => {
            setLocalRecordingSegments(0);
          }}
        />
        */}
      </LiveKitRoom>
    </div>
  );
};

export default LiveKitMeetingRoom;
